/**
 * The CMS page builder — `/v1/admin/cms/*`.
 *
 * **The preview is the server's render, never a second one.** Doc 39 §4.3 renders a draft through
 * the same `Renderer` the static export runs (CSR-041, `services/cms_preview.py`). A renderer here
 * would agree with the site right up until it did not — and the day it did not would be the day
 * somebody checked a page before publishing it. The preview writes nothing, so it invalidates
 * nothing.
 *
 * **Publishing and archiving are their own mutations, named for what they do.** Neither is a
 * status write: publish lints the whole page and proves its SEO template resolves; archive takes
 * a redirect decision. A `useUpdatePage({status})` would let a screen do either by sending a
 * string and skip the half that matters.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type CmsPageRow = components['schemas']['CmsPageRow'];
export type CmsPageDetail = components['schemas']['CmsPageDetail'];
export type CmsBlockRow = components['schemas']['CmsBlockRow'];
export type CmsFaqRow = components['schemas']['CmsFaqRow'];
export type CmsRevisionRow = components['schemas']['CmsRevisionRow'];
export type CmsRedirectRow = components['schemas']['CmsRedirectRow'];
type PageList = components['schemas']['CmsPageListResponse'];

export interface PageQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  page_type?: string;
  page_status?: string;
  q?: string;
  include_archived?: boolean;
  /** Only the soft-deleted drafts, each with what still holds it (R141). */
  deleted?: boolean;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function pageSearch(params: PageQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.page_type !== undefined) search.set('page_type', params.page_type);
  if (params.page_status !== undefined) search.set('page_status', params.page_status);
  if (params.q !== undefined && params.q !== '') search.set('q', params.q);
  if (params.include_archived) search.set('include_archived', 'true');
  if (params.deleted) search.set('deleted', 'true');
  return search.toString();
}

export function useCmsPages(params: PageQuery) {
  return useQuery({
    queryKey: keys.cms.pages(params),
    queryFn: () => authedClient.request<PageList>(`/v1/admin/cms/pages?${pageSearch(params)}`),
  });
}

export function useCmsPage(id: number | null) {
  return useQuery({
    queryKey: keys.cms.page(id ?? 0),
    queryFn: () => authedClient.request<CmsPageDetail>(`/v1/admin/cms/pages/${id}`),
    enabled: id !== null,
  });
}

function useCmsMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.cms.all() }),
  });
}

export function useCreatePage() {
  return useCmsMutation((body: Record<string, unknown>) =>
    authedClient.request<CmsPageRow>('/v1/admin/cms/pages', { method: 'POST', body }),
  );
}

export function useUpdatePage() {
  return useCmsMutation(({ id, ...body }: { id: number } & Record<string, unknown>) =>
    authedClient.request<CmsPageRow>(`/v1/admin/cms/pages/${id}`, { method: 'PATCH', body }),
  );
}

/** Lints the whole page and proves its template resolves. Named for that, not for a verb. */
export function usePublishPage() {
  return useCmsMutation((id: number) =>
    authedClient.request<CmsPageRow>(`/v1/admin/cms/pages/${id}/publish`, { method: 'POST' }),
  );
}

/** The draft as it would publish, as HTML. Rate-limited per admin; stored nowhere. */
export function usePreviewPage() {
  return useMutation<string, unknown, number>({
    mutationFn: (id) =>
      authedClient.request<string>(`/v1/admin/cms/pages/${id}/preview`, { method: 'POST', parse: 'text' }),
  });
}

/** Takes the redirect decision with it — there is no archive without one. */
export function useArchivePage() {
  return useCmsMutation(
    ({
      id,
      httpStatus,
      toPath,
      note,
    }: {
      id: number;
      httpStatus: number;
      toPath: string | null;
      note: string;
    }) =>
      authedClient.request<CmsRedirectRow>(`/v1/admin/cms/pages/${id}/archive`, {
        method: 'POST',
        body: { http_status: httpStatus, to_path: toPath, note },
      }),
  );
}

/**
 * Delete a draft made in error. Soft on the server — the path is freed and the revisions kept —
 * and refused 409 page_was_published for anything that was ever live: that one is archived.
 */
export function useDeletePage() {
  return useCmsMutation((id: number) =>
    authedClient.request<void>(`/v1/admin/cms/pages/${id}`, { method: 'DELETE' }),
  );
}

/**
 * Remove a deleted draft for good, with its blocks, SEO row, FAQs and its own revisions (R141).
 * It never went live, so no URL or cache moves. The server refuses 409 `still_referenced` while a
 * redirect rule or analytics row names it, and the reason is audited with the path and title.
 */
export function usePurgePage() {
  return useCmsMutation(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<void>(`/v1/admin/cms/pages/${id}/purge`, { method: 'POST', body: { reason } }),
  );
}

/** Archived → draft. The archive's redirect stays live until the page is published again. */
export function useRestorePage() {
  return useCmsMutation((id: number) =>
    authedClient.request<CmsPageRow>(`/v1/admin/cms/pages/${id}/restore`, { method: 'POST' }),
  );
}

export function useCreateBlock() {
  return useCmsMutation(({ pageId, ...body }: { pageId: number } & Record<string, unknown>) =>
    authedClient.request<CmsBlockRow>(`/v1/admin/cms/pages/${pageId}/blocks`, {
      method: 'POST',
      body,
    }),
  );
}

export function useUpdateBlock() {
  return useCmsMutation(({ id, ...body }: { id: number } & Record<string, unknown>) =>
    authedClient.request<CmsBlockRow>(`/v1/admin/cms/blocks/${id}`, { method: 'PATCH', body }),
  );
}

/** Drafts only — a live page hides a block instead. The server enforces it; this asks. */
export function useDeleteBlock() {
  return useCmsMutation((id: number) =>
    authedClient.request<void>(`/v1/admin/cms/blocks/${id}`, { method: 'DELETE' }),
  );
}

/** The complete order, never a move. Two operators reordering at once must not disagree. */
export function useReorderBlocks() {
  return useCmsMutation(({ pageId, blockIds }: { pageId: number; blockIds: number[] }) =>
    authedClient.request<CmsBlockRow[]>(`/v1/admin/cms/pages/${pageId}/blocks/reorder`, {
      method: 'POST',
      body: { block_ids: blockIds },
    }),
  );
}

export function useCreateFaq() {
  return useCmsMutation((body: Record<string, unknown>) =>
    authedClient.request<CmsFaqRow>('/v1/admin/cms/faqs', { method: 'POST', body }),
  );
}

export function useUpdateFaq() {
  return useCmsMutation(({ id, ...body }: { id: number } & Record<string, unknown>) =>
    authedClient.request<CmsFaqRow>(`/v1/admin/cms/faqs/${id}`, { method: 'PATCH', body }),
  );
}

/** A forward edit copying an old payload — never a rewind. */
export function useRestoreRevision() {
  return useCmsMutation((revisionId: number) =>
    authedClient.request<CmsPageRow>(`/v1/admin/cms/revisions/${revisionId}/restore`, {
      method: 'POST',
    }),
  );
}

/* ── The redirect map (E51) ───────────────────────────────────────────────────
 *
 * Same module as the pages, because the endpoints are on the same router for the same reason: a
 * redirect is the tail of a CMS page's life, and `useArchivePage` above writes most of these rows.
 * A `hooks/redirects.ts` would hold half of one table's story.
 */

export type RedirectRule = components['schemas']['RedirectRuleOut'];
export type RedirectCreate = components['schemas']['RedirectCreateIn'];
type RedirectList = components['schemas']['RedirectListResponse'];

/**
 * The whole map, newest first.
 *
 * Deliberately unpaginated by the server — the screen exists to scan for the rule that is wrong —
 * but it is capped, and the response carries `total` and `limit` so the console can say when it is
 * filtering over part of the table rather than all of it.
 *
 * ⛔ The endpoint takes `active_only`; this hook does not pass it. A switched-off rule is not a
 * hidden rule — it is the record of what an indexed URL used to do, which is the thing the map is
 * for — so the console always reads the whole table and tabs across it. Wiring the flag up here
 * with no caller would be a parameter whose only exercise is its own test.
 */
export function useRedirects() {
  return useQuery({
    queryKey: keys.cms.redirects(),
    queryFn: () => authedClient.request<RedirectList>('/v1/admin/cms/redirects'),
  });
}

/**
 * Add a rule by hand — the half of the map archiving does not write.
 *
 * ⛔ **Every refusal belongs to the server and reaches the screen verbatim.** The duplicate names
 * the rule that already answers for that URL; both chain refusals name the rule to fix; the path
 * refusals say which side is misspelled. A client that pre-judged any of them would eventually be
 * wrong and confident, and would also stop the operator seeing which rule to go and edit.
 */
export function useCreateRedirect() {
  return useCmsMutation((body: RedirectCreate) =>
    authedClient.request<RedirectRule>('/v1/admin/cms/redirects', { method: 'POST', body }),
  );
}

/** There is no delete. An indexed URL's history is the thing the map is for. */
export function useSetRedirectActive() {
  return useCmsMutation(({ id, isActive, reason }: { id: number; isActive: boolean; reason: string }) =>
    authedClient.request<RedirectRule>(`/v1/admin/cms/redirects/${id}/active`, {
      method: 'POST',
      body: { is_active: isActive, reason },
    }),
  );
}
